import { colors } from '@kmns-internal/furikake'
import { Close } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { addMonths, format, isValid, subMonths } from 'date-fns' // eslint-disable-line import/no-duplicates
import { ja } from 'date-fns/locale' // eslint-disable-line import/no-duplicates
import { FC, useEffect, useState } from 'react'
import { useApprovalReportsFilter } from 'src/features/approvals/hooks/useApprovalReportsFilter'

const getDefaultDateRange = (today: Date = new Date()): [Date, Date] => {
  const end = today
  const start = subMonths(end, 1)
  return [start, end]
}

const DateRangeFilter: FC = () => {
  const [[defaultFrom, defaultThrough]] = useState(getDefaultDateRange)
  const [fromDate, setFromDate] = useState<Date>(defaultFrom)
  const [throughDate, setThroughDate] = useState<Date>(defaultThrough)
  const { uiFilter, updateFilter, isDateFilterChanged } =
    useApprovalReportsFilter()

  useEffect(() => {
    if (!uiFilter?.from || !uiFilter?.through) return
    try {
      const from = new Date(uiFilter.from)
      const through = new Date(uiFilter.through)
      if (!isNaN(from.getTime()) && !isNaN(through.getTime())) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
        setFromDate(from)
        setThroughDate(through)
      }
    } catch {
      const [start, end] = getDefaultDateRange()
      setFromDate(start)
      setThroughDate(end)
    }
  }, [uiFilter?.from, uiFilter?.through])

  const handleFromDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return
    if (date > throughDate) {
      setFromDate(throughDate)
      setThroughDate(date)
      updateFilter({
        from: format(throughDate, 'yyyy-MM-dd'),
        through: format(date, 'yyyy-MM-dd'),
      })
    } else {
      setFromDate(date)
      updateFilter({
        from: format(date, 'yyyy-MM-dd'),
        through: format(throughDate, 'yyyy-MM-dd'),
      })
    }
  }

  const handleThroughDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return
    if (date < fromDate) {
      setFromDate(date)
      setThroughDate(fromDate)
      updateFilter({
        from: format(date, 'yyyy-MM-dd'),
        through: format(fromDate, 'yyyy-MM-dd'),
      })
    } else {
      setThroughDate(date)
      updateFilter({
        from: format(fromDate, 'yyyy-MM-dd'),
        through: format(date, 'yyyy-MM-dd'),
      })
    }
  }

  // disabled 制約：from は throughDate の 3ヶ月前〜throughDate、through は fromDate〜fromDate の 3ヶ月後
  const shouldDisableFromDate = (current: Date) => {
    const minDate = subMonths(throughDate, 3)
    return current < minDate || current > throughDate
  }

  const shouldDisableThroughDate = (current: Date) => {
    const maxDate = addMonths(fromDate, 3)
    return current < fromDate || current > maxDate
  }

  const resetToDefault = () => {
    const [start, end] = getDefaultDateRange()
    setFromDate(start)
    setThroughDate(end)
    updateFilter({
      from: undefined,
      through: undefined,
    })
  }

  const isChanged = isDateFilterChanged()
  const changedSx = {
    width: 164,
    ...(isChanged && {
      '& .MuiOutlinedInput-root': {
        backgroundColor: colors.white,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary.main,
        },
        '& input': {
          color: colors.primary.main,
        },
      },
    }),
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DatePicker
          value={fromDate}
          onChange={handleFromDateChange}
          shouldDisableDate={shouldDisableFromDate}
          orientation="portrait"
          format="yyyy/MM/dd"
          slotProps={{
            textField: {
              sx: changedSx,
              slotProps: {
                htmlInput: { placeholder: '開始日' },
              },
              size: 'small',
            },
            toolbar: { hidden: true },
          }}
        />
        <Typography>〜</Typography>
        <DatePicker
          value={throughDate}
          onChange={handleThroughDateChange}
          shouldDisableDate={shouldDisableThroughDate}
          orientation="portrait"
          format="yyyy/MM/dd"
          slotProps={{
            textField: {
              sx: changedSx,
              slotProps: {
                htmlInput: { placeholder: '終了日' },
              },
              size: 'small',
            },
            toolbar: { hidden: true },
          }}
        />
        {isChanged && (
          <Tooltip title="日付をリセット">
            <IconButton
              size="small"
              onClick={resetToDefault}
              aria-label="日付をリセット"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </LocalizationProvider>
  )
}

export default DateRangeFilter
